import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import AdminLayout from "@/components/admin/AdminLayout";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { ProfileDraftComparison } from "@/components/athlete/ProfileDraftComparison";
import { isReviewPilot } from "@/lib/featureFlags";
import { DRAFT_FIELD_LABELS } from "@/lib/profileDraft";

type Review = {
  draft_id: string; draft_hash: string; proposed: Record<string, any>; live: Record<string, any>;
  live_changed_since_draft: boolean; approval_valid: boolean; changes_requested: string | null;
};
const TEXT_FIELDS = ["name", "first_name", "last_name", "sport", "tagline", "jersey_number", "position_long", "height",
  "hometown", "college", "occupation", "mantra_quote", "instagram_url", "tiktok_url", "youtube_url", "facebook_url", "linkedin_url"];
const LONG_FIELDS = ["bio", "story", "why_i_play", "causes_text", "partnership_interests", "support_blurb"];
const err = (e: any) => e?.message ?? "Something went wrong";

/** Admin draft editor (pilot athletes only). Edits never touch the live profile until launch. */
const AdminProfileDraft = () => {
  const { id = "" } = useParams();
  const [name, setName] = useState("");
  const [review, setReview] = useState<Review | null | undefined>(undefined);
  const [form, setForm] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const [a, r] = await Promise.all([
      supabase.from("athletes").select("name").eq("id", id).maybeSingle(),
      supabase.rpc("profile_draft_review", { _athlete_id: id }),
    ]);
    setName((a.data as any)?.name ?? "");
    if (r.error) { toast.error(err(r.error)); setReview(null); return; }
    const rv = (r.data as unknown as Review) ?? null;
    setReview(rv);
    if (rv) setForm(Object.fromEntries([...TEXT_FIELDS, ...LONG_FIELDS].map((k) => [k, rv.proposed[k] == null ? "" : String(rv.proposed[k])])));
  };
  useEffect(() => { if (isReviewPilot(id)) load(); }, [id]);

  const run = async (fn: () => PromiseLike<{ error: any }>, ok: string) => {
    setBusy(true);
    try { const { error } = await fn(); if (error) throw error; toast.success(ok); await load(); }
    catch (e) { toast.error(err(e)); } finally { setBusy(false); }
  };

  if (!isReviewPilot(id)) {
    return <AdminLayout><p className="p-4 text-sm">The draft editor is only enabled for pilot athletes.</p></AdminLayout>;
  }

  const save = () => {
    if (!review) return;
    const changes: Record<string, string | null> = {};
    for (const [k, v] of Object.entries(form)) {
      const next = v.trim() === "" ? null : v;
      const cur = review.proposed[k] == null ? null : String(review.proposed[k]);
      if (next !== cur) changes[k] = next;
    }
    if (Object.keys(changes).length === 0) return toast.message("No changes to save");
    run(() => supabase.rpc("admin_update_profile_draft", { _draft_id: review.draft_id, _changes: changes as any }), "Draft saved — live profile unchanged");
  };

  return (
    <AdminLayout>
      <div className="max-w-4xl space-y-4">
        <div>
          <Link to="/admin/athletes/overview" className="text-sm text-muted-foreground">← Athlete overview</Link>
          <h1 className="font-display text-2xl mt-1">Profile draft — {name}</h1>
          <p className="text-sm text-muted-foreground">Private draft — fans never see it. The live profile stays exactly as it is until the athlete approves and you launch.</p>
        </div>
        {review === undefined ? <p className="text-sm">Loading…</p> : !review ? (
          <div className="rounded-2xl border border-border bg-card p-5 space-y-3">
            <p className="text-sm">No draft yet. Starting one copies her current live text into a private draft. Nothing on her live profile changes.</p>
            <Button disabled={busy} onClick={() => run(() => supabase.rpc("admin_create_profile_draft", { _athlete_id: id }), "Draft created from the live profile")}>Start a private draft</Button>
          </div>
        ) : (
          <>
            <ProfileDraftComparison live={review.live} proposed={review.proposed}
              liveChangedSinceDraft={review.live_changed_since_draft} approvalValid={review.approval_valid} audience="admin" />
            {review.changes_requested && <p className="rounded-xl bg-muted p-3 text-sm">Athlete note: “{review.changes_requested}”</p>}
            <section className="rounded-2xl border border-border bg-card p-5 space-y-3">
              <h2 className="font-semibold">Edit draft</h2>
              <p className="text-xs text-muted-foreground">Saving any edit cancels a previous athlete approval.</p>
              <p className="rounded-lg bg-muted p-3 text-xs">Text fields only in this pilot. Photos, gallery, videos, Q&amp;A and booking buttons are not edited here — launching keeps her current live versions of those exactly as they are.</p>
              <div className="grid gap-3 sm:grid-cols-2">
                {TEXT_FIELDS.map((k) => (
                  <label key={k} className="text-sm">{DRAFT_FIELD_LABELS[k]}
                    <input value={form[k] ?? ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="mt-1 w-full rounded-lg border border-input bg-background p-2" />
                  </label>
                ))}
              </div>
              {LONG_FIELDS.map((k) => (
                <label key={k} className="block text-sm">{DRAFT_FIELD_LABELS[k]}
                  <textarea rows={4} value={form[k] ?? ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="mt-1 w-full rounded-lg border border-input bg-background p-2" />
                </label>
              ))}
              <Button disabled={busy} onClick={save}>Save draft</Button>
            </section>
            <section className="rounded-2xl border border-border bg-card p-5 space-y-3" aria-labelledby="launch-h">
              <h2 id="launch-h" className="font-semibold">Launch</h2>
              <p className="text-sm text-muted-foreground">
                {review.live_changed_since_draft ? "The live profile changed after this draft was started. Re-base the draft on the current live profile, check it, and ask the athlete to approve again."
                  : review.approval_valid ? "The athlete approved this exact version. Launching copies these text changes to her live profile. It does not publish, unpublish or change who can see her profile."
                  : "Waiting for the athlete to approve this exact version. Only an admin can launch, and only after her approval and consent are recorded."}
              </p>
            <div className="flex flex-wrap gap-2">
              {review.live_changed_since_draft && (
                <Button variant="outline" disabled={busy} onClick={() => run(() => supabase.rpc("admin_rebase_profile_draft", { _draft_id: review.draft_id }), "Draft re-based on the current live profile — athlete must approve again")}>Re-base on current live profile</Button>
              )}
              <Button disabled={busy || !review.approval_valid || review.live_changed_since_draft}
                onClick={() => window.confirm("Launch this approved draft to the live profile?") && run(() => supabase.rpc("admin_launch_profile_draft", { _draft_id: review.draft_id }), "Approved draft launched")}>
                Launch approved draft
              </Button>
              <Button variant="outline" disabled={busy}
                onClick={() => window.confirm("Discard this draft? It stays in history.") && run(() => supabase.rpc("admin_discard_profile_draft", { _draft_id: review.draft_id }), "Draft discarded")}>
                Discard draft
              </Button>
            </div>
            </section>
          </>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminProfileDraft;
